/**
 * 日志工具
 * 支持输出到控制台和文件（浏览器环境模拟）
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: number;
  level: LogLevel;
  message: string;
  data?: any;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  /**
   * 记录日志
   */
  private log(level: LogLevel, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      message,
      data
    };

    this.logs.push(entry);

    // 限制日志数量
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // 输出到控制台
    const timestamp = new Date(entry.timestamp).toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, data || '');
        break;
      case 'info':
        console.info(prefix, message, data || '');
        break;
      case 'warn':
        console.warn(prefix, message, data || '');
        break;
      case 'error':
        console.error(prefix, message, data || '');
        break;
    }
  }

  /**
   * Debug 级别日志
   */
  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  /**
   * Info 级别日志
   */
  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  /**
   * Warn 级别日志
   */
  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  /**
   * Error 级别日志
   */
  error(message: string, data?: any) {
    this.log('error', message, data);
  }

  /**
   * 获取所有日志
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * 清空日志
   */
  clear() {
    this.logs = [];
  }

  /**
   * 导出日志为文本
   */
  exportAsText(): string {
    return this.logs
      .map(entry => {
        const timestamp = new Date(entry.timestamp).toISOString();
        const data = entry.data ? ` ${JSON.stringify(entry.data)}` : '';
        return `[${timestamp}] [${entry.level.toUpperCase()}] ${entry.message}${data}`;
      })
      .join('\n');
  }

  /**
   * 下载日志文件
   */
  downloadLogs(filename: string = 'puppycad-logs.txt') {
    const content = this.exportAsText();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// 导出单例
export const logger = new Logger();
export default logger;
